import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle } from '@angular/material/list';
import { MatSelect } from '@angular/material/select';
import { b64toBlob } from '@app/api/core/utils/file.util';
import { extractKontaktOptions } from '@app/api/core/utils/helper/kontakt-helper';
import { getKontaktPreferHaupt, personHasKontakt } from '@app/api/core/utils/helper/person-helper';
import { ReactiveDialogContentComponent } from '@app/shared/components/dialog/reactive-dialog/base/reactive-dialog-content/reactive-dialog-content.component';
import { getFormErrors } from '@app/shared/services/form/form-error.util';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { KeyValue, ObjektTyp, Person, Polizze } from '@o30/entities';
import FileSaver from 'file-saver';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, first, take } from 'rxjs/operators';
import { ValidatorConfig } from '../../../dynamic-form/validator-config.model';
import { DataService } from '../../../services/data/data.service';
import { DialogService } from '../../../services/ui/dialog/dialog.service';
import { MessageType } from '../../../services/ui/message/message';
import { MessageService } from '../../../services/ui/message/message.service';
import { BasicCheckboxComponent } from '../../form/basic-checkbox/basic-checkbox.component';
import { BasicInputComponent } from '../../form/basic-input/basic-input.component';
import { SpacerComponent } from '../../spacer/spacer.component';
import { EmailValidator } from '../../validators/email-validator';
import { AbstractReactiveDialogComponent } from '../reactive-dialog/base/abstract-reactive-dialog-component';

@UntilDestroy()
@Component({
  selector:        'app-fahrzeugbesichtigung-dialog',
  templateUrl:     './fahrzeugbesichtigung-dialog.component.html',
  styleUrls:       ['./fahrzeugbesichtigung-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports:         [
    ReactiveDialogContentComponent,
    SpacerComponent,
    MatButtonToggleGroup,
    FormsModule,
    ReactiveFormsModule,
    MatButtonToggle,
    NgIf,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    NgFor,
    BasicInputComponent,
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemTitle,
    BasicCheckboxComponent,
    MatButton,
    AsyncPipe
  ]
})
export class FahrzeugbesichtigungDialogComponent extends AbstractReactiveDialogComponent<never, never, FahrzeugbesichtigungDialogConfig, never>
  implements OnInit {

  private dataService = inject(DataService);
  private messageService = inject(MessageService);
  private llapi = inject(LowLevelApi);
  private legacyDialogService = inject(DialogService);
  private ref = inject(ChangeDetectorRef);

  personId: Person['id'];
  polizzeId: Polizze['id'];

  typeSelection: UntypedFormControl;
  emailDropdown: UntypedFormControl;
  emailOptions$: Observable<string[]>;

  personname: string;
  email: string;

  sendRunning: boolean;
  confirmation: boolean;

  @Input() files: KeyValue<Blob>[];

  @ViewChild('ngForm') ngForm: NgForm;

  public constructor() {
    super();
  }

  ngOnInit(): void {
    this.initConfig();
    this.initFiles();
    this.initForm();
  }

  private initConfig() {
    const config = this.config;
    const fromConfig = [
      // 'type',
      'personId',
      'polizzeId'
    ];
    if (config) {
      for (const key of fromConfig) {
        if (config[key]) {
          this[key] = config[key];
        }
      }
    }
  }

  private initFiles() {
    this.dataService.getPolizzeFahrzeugBesichtigungFiles(this.polizzeId)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(_payload => {
        if (this.messageService.handlePayloadReturnSuccess(_payload)) {
          this.files = _payload.data.map(_item => ({
            key:   _item.key,
            value: b64toBlob(_item.value, 'application/pdf')
          }));
        }
      });
  }

  private updateTitle(type: 'mail' | 'print') {
    this.dialogInputData.title = 'Information Gebrauchtwagenbesichtigung ' +
      (type === 'print'
        ? 'aushändigen'
        : 'versenden');
  }

  private initForm() {
    const config = this.config;
    this.updateTitle(this.config.type);
    this.typeSelection = new UntypedFormControl(config.type ? config.type : 'mail');
    this.typeSelection.valueChanges.pipe(
        untilDestroyed(this),
        distinctUntilChanged())
      .subscribe(this.updateTitle.bind(this));
    this.emailDropdown = new UntypedFormControl();
    this.emailDropdown.valueChanges.pipe(
        untilDestroyed(this),
        distinctUntilChanged())
      .subscribe(_newValue => this.email = _newValue);
    this.emailDropdown.valueChanges.pipe(
        untilDestroyed(this))
      .subscribe(() => {
        this.emailDropdown.setValue(undefined, {
          onlySelf:  true,
          emitEvent: false
        });
      });
    if (this.personId) {
      this.llapi.patchValue('layout.spinner', true);
      this.dataService.getPerson(this.personId)
        .pipe(
          untilDestroyed(this),
          first()
        )
        .subscribe(_result => {
            if (_result?.data) {
              const person = _result.data;
              this.assignEmail(person);
              this.assignName(person);
              this.emailOptions$ = of(extractKontaktOptions(person.kontaktdaten));
              this.ref.detectChanges();
            }
            this.llapi.patchValue('layout.spinner', false);
          }
        );
    }
  }

  get type(): 'mail' | 'print' {
    return this.typeSelection.value;
  }

  get config(): FahrzeugbesichtigungDialogConfig {
    return this.dialogInputData?.context;
  }

  get infotext(): string {
    const info = {
      mail:  '',
      print: 'Sie können die Information zur Gebrauchtwagenbesichtigung in Ihrem PDF-Programm öffnen und anschließend ausdrucken.'
    };
    return info[this.type];
  }

  get confirmationText(): string {
    const info = {
      mail:  'Ich bestätige, dass der Kunde vorab der Übermittlung an die erfasste E-Mail-Adresse ausdrücklich zugestimmt hat.',
      print: 'Ich bestätige, dass ich die Information zur Gebrauchtwagenbesichtigung in Papier übergeben habe.'
    };
    return info[this.type];
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--events--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  onConfirmClick(): void {
    if (this.type === 'mail') {
      const errors = this.getErrors();
      if (errors?.length) {
        this.messageService.showSnackBar({
          text: 'Überprüfen Sie die Eingabe',
          type: MessageType.ERROR,
          icon: 'error'
        });
        return;
      }
    }
    this.sendRunning = true;
    this.llapi.patchValue('layout.spinner', true);
    // this.dataService.sendMail(this.email, 'fahrzeugbesichtigung', this.polizzeId)
    this.dataService.putPolizzeFahrzeugBesichtigung(
        this.polizzeId,
        this.type === 'mail',
        this.type === 'mail' ? this.email : '-')
      .pipe(
        untilDestroyed(this),
        first()
      )
      .subscribe(_payload => {
        this.llapi.patchValue('layout.spinner', false);
        this.sendRunning = false;
        if (this.messageService.handlePayloadReturnSuccess(_payload)) {
          this.dialogRef.close({status: 'success'});
        } else {
          this.ref.detectChanges();
        }
      });
  }

  onEditKunde(): void {
    this.legacyDialogService.displayPersonErfassenDialog({
        objektTyp:  ObjektTyp.KUNDE,
        objId:      this.personId,
        kfa:        false,
        skipPolice: true
      })
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(_dialogErfassenResult => {
        if (_dialogErfassenResult?.data) {
          const person = _dialogErfassenResult.data;
          this.assignEmail(person);
        }
      });
  }

  private assignEmail(person: Person): void {
    if (personHasKontakt(person, 'email')) {
      this.email = getKontaktPreferHaupt(person, 'email');
    }
  }

  private assignName(person: Person): void {
    const name = [];
    if (person.vorname) {
      name.push(person.vorname.value);
    }
    if (person.name) {
      name.push(person.name.value);
    }
    this.personname = name.join(' ');
  }

  private getErrors(): ValidationErrors[] {
    return getFormErrors(this.ngForm);
  }

  get emailValidator(): ValidatorConfig {
    return new Map(
      [
        [
          EmailValidator,
          []
        ],
        [
          Validators.required,
          []
        ]
      ]
    );
  }

  public openFile(file: KeyValue<Blob>): void {
    FileSaver.saveAs(file.value, file.key);
  }
}

export interface FahrzeugbesichtigungDialogConfig {
  type: 'mail' | 'print';
  personId: Person['id'];
  polizzeId: Polizze['id'];
}
